import { useRef, useState } from 'react';

type Offset = { x: number; y: number };

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Makes an absolutely-positioned element draggable via translate offset, clamped so it
 * never leaves the bounds of its DOM parent (e.g. keeps a PiP overlay inside the image
 * viewer / map frame instead of the whole viewport or a sibling panel).
 */
export const useDraggableWithinParent = () => {
    const ref = useRef<HTMLDivElement>(null);
    const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });

    const handleDragStart = (e: React.MouseEvent) => {
        const boxEl = ref.current;
        const container = boxEl?.parentElement;
        if (!boxEl || !container) return;

        const boxRect = boxEl.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        const minOffsetX = offset.x + (containerRect.left - boxRect.left);
        const maxOffsetX = offset.x + (containerRect.right - boxRect.right);
        const minOffsetY = offset.y + (containerRect.top - boxRect.top);
        const maxOffsetY = offset.y + (containerRect.bottom - boxRect.bottom);

        const startX = e.clientX - offset.x;
        const startY = e.clientY - offset.y;

        const onMove = (ev: MouseEvent) => {
            setOffset({
                x: clamp(ev.clientX - startX, minOffsetX, maxOffsetX),
                y: clamp(ev.clientY - startY, minOffsetY, maxOffsetY),
            });
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        e.preventDefault();
    };

    return { ref, offset, handleDragStart };
};
